import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  UserPlus, FileCheck, AlertTriangle, Gavel, Landmark, FolderSearch,
  ListTodo, CalendarDays, Send, Scale, CheckCircle2, Inbox, DollarSign, HandHelping, ClipboardCheck,
} from "lucide-react";
import { api } from "../lib/api";

const CARDS = [
  { key: "new_leads", label: "New Leads", icon: UserPlus, tone: "teal" },
  { key: "intakes_received", label: "New Intakes", icon: Inbox, tone: "teal" },
  { key: "urgent_cases", label: "Urgent Cases (RFE / Action)", icon: AlertTriangle, tone: "red" },
  { key: "court_deadlines", label: "Court Deadlines", icon: Gavel, tone: "red" },
  { key: "uscis_deadlines", label: "USCIS Deadlines", icon: Landmark, tone: "champagne" },
  { key: "docs_awaiting_review", label: "Documents Awaiting Review", icon: FolderSearch, tone: "champagne" },
  { key: "client_tasks_overdue", label: "Client Tasks Overdue", icon: ListTodo, tone: "red" },
  { key: "upcoming_appointments", label: "Upcoming Appointments", icon: CalendarDays, tone: "sage" },
  { key: "ready_for_attorney", label: "Ready for Attorney Review", icon: Scale, tone: "teal" },
  { key: "ready_to_file", label: "Cases Ready to File", icon: Send, tone: "sage" },
  { key: "active_cases", label: "Active Cases", icon: FileCheck, tone: "teal" },
  { key: "completed_cases", label: "Recently Completed", icon: CheckCircle2, tone: "sage" },
  { key: "payments_outstanding", label: "Payments Outstanding", icon: DollarSign, tone: "champagne" },
  { key: "service_requests_new", label: "New Service Requests", icon: HandHelping, tone: "teal" },
  { key: "client_intakes_submitted", label: "Client Intakes to Review", icon: ClipboardCheck, tone: "champagne" },
];

const TONES = {
  teal: "bg-teal-light text-teal",
  sage: "bg-sage-light text-sage",
  champagne: "bg-champagne-light text-champagne-dark",
  red: "bg-red-950/60 text-red-300",
};

export default function StaffDashboard() {
  const [overview, setOverview] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestStatuses, setRequestStatuses] = useState([]);

  const loadRequests = () => api.get("/service-requests").then((r) => setRequests(r.data.slice(0, 10))).catch(() => {});

  useEffect(() => {
    api.get("/staff/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/intakes").then((r) => setIntakes(r.data.slice(0, 8))).catch(() => {});
    api.get("/contact").then((r) => setContacts(r.data.slice(0, 5))).catch(() => {});
    api.get("/meta/options").then((r) => setRequestStatuses(r.data.request_statuses)).catch(() => {});
    loadRequests();
  }, []);

  return (
    <div className="space-y-8" data-testid="staff-dashboard">
      <div>
        <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal">Command Center</p>
        <h1 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight mt-1">Today at Full Circle</h1>
      </div>

      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="staff-metrics">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-surface border border-line rounded-xl p-5 shadow-sm" data-testid={`metric-${c.key.replace(/_/g, "-")}`}>
            <div className={`h-9 w-9 rounded-lg ${TONES[c.tone]} flex items-center justify-center mb-3`}>
              <c.icon className="h-4 w-4" />
            </div>
            <p className="text-2xl font-serif font-semibold text-navy">{overview ? overview[c.key] : "—"}</p>
            <p className="text-xs text-mutedink mt-1">{c.label}</p>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="recent-intakes">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-sans font-semibold text-navy">Latest intakes</h2>
            <Link to="/staff/cases" className="text-xs font-medium text-teal hover:underline" data-testid="view-all-cases-link">View all</Link>
          </div>
          {intakes.length === 0 ? (
            <p className="text-sm text-mutedink">No intakes yet. New "Start Your Journey" submissions appear here.</p>
          ) : (
            <ul className="space-y-3">
              {intakes.map((i) => (
                <li key={i.intake_id} className="flex items-start justify-between gap-3 text-sm border-b border-line last:border-0 pb-3" data-testid={`intake-row-${i.intake_id}`}>
                  <div>
                    <p className="font-medium text-navy">{i.contact?.name || "Unnamed"}</p>
                    <p className="text-xs text-mutedink">{(i.help_needed || []).join(", ") || "—"}</p>
                  </div>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-teal-light text-teal whitespace-nowrap">{i.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="contact-messages">
          <h2 className="text-base font-sans font-semibold text-navy mb-4">Website contact messages</h2>
          {contacts.length === 0 ? (
            <p className="text-sm text-mutedink">No contact form messages yet.</p>
          ) : (
            <ul className="space-y-3">
              {contacts.map((c) => (
                <li key={c.contact_id} className="text-sm border-b border-line last:border-0 pb-3">
                  <p className="font-medium text-navy">{c.name} <span className="text-xs text-mutedink font-normal">· {c.topic}</span></p>
                  <p className="text-xs text-mutedink mt-0.5 line-clamp-2">{c.message}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="service-requests-section">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Latest service requests</h2>
        {requests.length === 0 ? (
          <p className="text-sm text-mutedink">No client service requests yet.</p>
        ) : (
          <ul className="space-y-3">
            {requests.map((r) => (
              <li key={r.request_id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm border-b border-line last:border-0 pb-3" data-testid={`dashboard-request-${r.request_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-navy">{r.user_name} <span className="text-xs font-normal text-mutedink">· {r.service_type}{r.urgency === "urgent" && <span className="text-champagne-dark font-semibold"> · URGENT</span>}</span></p>
                  <p className="text-xs text-mutedink mt-0.5">{new Date(r.created_at).toLocaleDateString()}{r.details && ` · ${r.details}`}</p>
                </div>
                <select
                  value={r.status}
                  onChange={async (e) => { await api.patch(`/service-requests/${r.request_id}`, { status: e.target.value }); loadRequests(); }}
                  className="h-8 rounded-lg border border-line bg-ivory px-2 text-xs text-navy"
                  data-testid={`dashboard-request-status-${r.request_id}`}
                >
                  {requestStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
